import { File } from './file';
import { Track } from './track';
import { EventTopic } from './eventTopic';
import { User } from './user';
import { Author } from './author';
import { Type, plainToClass } from 'class-transformer';
import { SubmissionStatus } from '../enum/submission.status';
import { Form } from './form';
import { Answer } from './answer';
import { Event } from './event';
// `Session` referenced by the `@Type` factory + a type-cast below.
// Imported as type-only to keep the module graph acyclic — the runtime
// constructor is resolved via the class registry. See
// `_class-registry.ts` for the full rationale.
import type { Session } from './session';
import { register, getClass } from './_class-registry';
import { BaseModel, toDayjs } from './baseModel';
import { PaperGroup } from './paper.group';
import { TPCGroup } from './tpc.group';
import { PrintingProblem } from './printing.problem';
import { Transform } from 'class-transformer';
import * as dayjs from 'dayjs';
import { FormFieldType } from '../enum/form.field.type';
import { ReviewStatus } from '../enum/review.status';
import { PaperReviewHistory } from './paper-review-history';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { FileRules } from './file.rules';
import { Rebuttal } from './rebuttal';
import { DiscussionMessage } from './discussionMessage';
import { SubmissionHistory } from './submission-history';
import { UserReview } from './user.review';

export class Submission extends BaseModel {
  constructor() { super(); }

  get trackID(): number {
    return (<Track>this.track).id ? (<Track>this.track).id : (<number>this.track);
  }

  get trackName(): string {
    return (<Track>this.track).id ? (<Track>this.track).name : (<number>this.track).toString();
  }

  @Type(() => Author)
  authors: Array<Author>;

  @Type(() => Form)
  formSubmission: Form;

  @Type(() => Form)
  form: Form;

  @Type(() => File)
  files: Array<File>;

  @Type(() => Answer)
  formAnswer: Array<Answer>;

  @Type(() => PaperGroup)
  paperGroup: PaperGroup | number;

  tpcGroup: any;

  @Type(() => User)
  userClaims: Array<User>;

  @Type(() => User)
  usersByInterest: Array<User>;

  // Lazy class lookup via registry (see import comment above).
  @Type(() => getClass<typeof Session>('Session'))
  session: Session | number;

  visible = true;
  favorite = false;

  // has reviews
  hr: boolean;
  flagReviewer: boolean;

  /** Id de quem CRIOU a submissão. O backend deriva do log (não há campo no
   *  modelo) e recusa uma edição que tire o criador da lista de autores —
   *  exceto para chair. Aqui serve só p/ não OFERECER o [x] nesse autor.
   *  `null` em submissões legadas, sem log de criação. */
  creator: number | null;

  contributions = '';

  //Publication status
  authorRegistration: boolean;
  copyrightIEEE: boolean;
  checkListCustom:  Array<{
    fieldId: number,
    status: boolean
  }>;

  // No-show tri-estado (recurso ligado por `event.noShowEnable`):
  //   null/undefined = não definido, false = apresentado, true = no-show.
  noShow: boolean;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  @Type(() => Review)
  reviews: Array<Review | number>;
  rebuttal: string;

  @Type(() => DiscussionMessage)
  discussionMessages: Array<DiscussionMessage> = [];

  status: SubmissionStatus;
  title: string;
  abstract: string;

  @Type(() => EventTopic)
  topics: Array<EventTopic> = [];

  @Type(() => Track)
  track: Track | number;

  @Type(() => Event)
  event: Event;

  @Transform(toDayjs)
  statusChangedAt: dayjs.Dayjs;

  @Transform(toDayjs)
  submittedAt: dayjs.Dayjs;

  data(): object {
    return {
      status: this.status.toUpperCase(),
      title: this.title,
      abstract: this.abstract,
      track: this.trackID,
      authors: this.authors.map(author => ({ order: author.order, user: author.user.id })),
      topics: this.topics.map(e => e.id),
      contributions: this.contributions,
      formAnswer: this.formAnswer
    };
  }

  updateData(data: {
    status: SubmissionStatus, title: string, abstract: string,
    topics: EventTopic[], track: Track, authors: { user: User, order: number }[],
    formAnswers: Array<Answer>
  }): void {
    this.status = data.status;
    this.title = data.title;
    this.abstract = data.abstract;
    this.track = data.track;
    this.authors = data.authors;
    this.topics = data.topics;
    this.formAnswer = data.formAnswers;
  }

  get reviewBlocked(): boolean {
    switch (this.status) {
      case SubmissionStatus.ACCEPTED:
      case SubmissionStatus.REJECTED:
      case SubmissionStatus.WITHDRAWN:
      // case SubmissionStatus.PUBLISHED:
        return true;
      default:
        return false;
    }
  }

  public get editable(): boolean {
    return !(<Track>this.track).closed || 
    ((<Track>this.track).editPendingSubmissions && this.status === SubmissionStatus.PENDING) ||
    ((<Track>this.track).editActiveSubmissions && this.status === SubmissionStatus.ACTIVE) ||
    ((<Track>this.track).editRejectedSubmissions && this.status === SubmissionStatus.REJECTED) ||
    ((<Track>this.track).editAcceptedSubmissions && this.status === SubmissionStatus.ACCEPTED);
  }

  public get withdrawable(): boolean {
    // If it is under revision, checks if it's enabled to withdraw papers in this stage
    if (this.hr && (this.status === SubmissionStatus.PENDING || this.status === SubmissionStatus.ACTIVE)){
      if(!(<Track>this.track).withdrawUnderReviewSubmissions) return false
      // else, just check if its enabled for its status
    }
    return !(<Track>this.track).closed || 
    ((<Track>this.track).withdrawPendingSubmissions && this.status === SubmissionStatus.PENDING) ||
    ((<Track>this.track).withdrawActiveSubmissions && this.status === SubmissionStatus.ACTIVE) ||
    ((<Track>this.track).withdrawRejectedSubmissions && this.status === SubmissionStatus.REJECTED) ||
    ((<Track>this.track).withdrawAcceptedSubmissions && this.status === SubmissionStatus.ACCEPTED);
  }

  public get deletedTrack(): boolean {
    if(this.track){
      return (<Track>this.track).isDeleted
    }else return false
  }

  get mainAuthor(): User {
    return this.authors[0].user;
  }

  isAuthor(id: number): boolean {
    return !!this.authors.find(e => e.user.id === id);
  }

  hasClaim(id: number): boolean {
    return this.userClaims.some(u => (typeof u === 'number' ? u : u.id) == id);
  }

  isReviewer(userId: number): boolean {
    return this.reviews.some(review => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (review instanceof Review) {
        return review.userId === userId && review.status !== ReviewStatus.DELETED;
      }
      return false;
    });
  }

  isAssigner(userId: number): boolean {
    return this.reviews.some(review => {
      if (review instanceof Review) {
        return review.assignedByUserId === userId && review.status !== ReviewStatus.DELETED;
      }
      return false;
    });
  }

  isReviewerOrAssigner(userId: number): boolean {
    return this.isReviewer(userId) || this.isAssigner(userId);
  }

  isSessionChair(userId: number): boolean {
    if (this.session) {
      // Was `typeof(this.session, Session)` — a no-op comma expression
      // that's always truthy. Replaced with an `instanceof` against the
      // registry-resolved class. Avoids referencing `Session` as a
      // runtime value here (it's type-only now to keep the module
      // graph acyclic — see import comment).
      if (this.session instanceof getClass<any>('Session')) {
        if ((<Session>this.session).chair) {
          let u = (<Session>this.session).chair;
          if ((typeof u === 'number' ? u : u.id) == userId){
            return true;
          }
        }
      }
    }
    return false;
  }

  get authorNames(): string {
    return this.authors.map(a => a.user.name).join(', ');
  }

  get authorEmails(): string {
    return this.authors.map(a => a.user.email).join(', ');
  }

  findReviewIndex(reviewId: number): number {
    return this.reviews.findIndex(r => {
      if (r instanceof Review) {
        return r.id === reviewId;
      }
      return r === reviewId;
    });
  }

  getSubmissionFormAnswers(form?: Form): Array<{
    order: number, shortLabel: string, description: string, required: boolean,
    type: FormFieldType, options: Array<{ label: string, selected: boolean }> | null,
    answers: Array<string>
  }> {
    if (form) {
      this.formSubmission = form;
    }
    return this.formSubmission.fields.map(field => {
      const hasFormAnswer = !!this.formAnswer;

      let fieldAnswers;
      if (hasFormAnswer) {
        fieldAnswers = this.formAnswer.filter(a => a.formField === field.id);
      } else {
        fieldAnswers = field.answers;
      }

      const isChoice = field.type == FormFieldType.CHECKBOX || field.type == FormFieldType.RADIO;

      return {
        order: field.order,
        shortLabel: field.shortLabel,
        description: field.description,
        required: field.required,
        type: field.type,
        // Campos de escolha (radio/checkbox): TODAS as opções + quais foram marcadas.
        options: isChoice ? (field.choices || []).map(choice => {
          const ans = fieldAnswers.find(a => a.fieldChoice === choice.id);
          const selected = field.type == FormFieldType.CHECKBOX
            ? (!!ans && (ans.value === 'true' || (ans.value as any) === true))
            : !!ans;
          return { label: choice.description, selected };
        }) : null,
        answers: fieldAnswers.map(answer => {
          // Is assignable field
          if (answer.fieldChoice) {
            const choice = field.choices.find(c => answer.fieldChoice === c.id);
            // Deletes unselected options
            if (field.type == FormFieldType.CHECKBOX && answer.value != "true") {
              return null;
            }
            return `${choice.description}`;
          }
          // Is text field
          return `${answer.value}`;
        })
      };
    });
  }

  getOpenTrackFiles(): Array<FileRules> {
    return this.track instanceof Track ? this.track.trackFiles.filter(t => t.open) : undefined;
  }
}

export { SubmissionStatus as SubmissionStatus };

export class DeletedSubmission extends Submission {
  @Type(() => Event)
  public event: Event;

  @Type(() => User)
  public deletedBy: User;

  @Transform(toDayjs)
  public deletedAt: dayjs.Dayjs;
  
  public previousStatus: SubmissionStatus;
}

export class Review extends BaseModel {
  @Type(() => Submission)
  submission: Submission;

  @Type(() => Form)
  form: Form;

  @Type(() => Answer)
  answers: Array<Answer> = [];

  @Type(() => User)
  assignedBy?: User | number;

  @Type(() => Boolean)
  isFirstAssign: boolean;

  /**
   * Revisão anônima: o revisor é invisível para os TPC Chairs, e só para eles.
   * Marcada pelo SERVIDOR na delegação, quando um membro do TPC repassa a
   * revisão de uma submissão que tem um chair entre os autores. Read-only na
   * API — o front nunca escreve este campo.
   *
   * Quando `true`, o backend já entrega `user: null` para quem não pode ver a
   * identidade; o front usa a flag apenas para EXPLICAR a ausência (rótulo
   * "Revisor anônimo" no paper-review-list, ribbon preta no card do delegante),
   * distinguindo-a do outro eixo de anonimato ("Revisor #N", que vem da config
   * `tpcViewReviewIdentity` do evento).
   */
  @Type(() => Boolean)
  anonymity: boolean;

  @Type(() => User)
  user?: User | number;

  @Transform(toDayjs)
  public dueAt: dayjs.Dayjs;

  public visible = true;
  public favorite = false;
  public underDraft = false;

  public eventId: number;

  @Transform(toDayjs)
  public assignedAt: dayjs.Dayjs;

  @Transform(toDayjs)
  public remindedAt: dayjs.Dayjs;

  @Transform(toDayjs)
  public confirmedAt: dayjs.Dayjs;

  @Transform(toDayjs)
  public completedAt: dayjs.Dayjs;

  @Type(() => PrintingProblem)
  printingProblems: Array<PrintingProblem>;

  isReviewerDistributor: boolean;

  reviewQuestionVisibility = this.wasAnswered();

  constructor(
    public status: ReviewStatus
  ) { super(); }

  public wasAnswered(): boolean {
    return this.status === ReviewStatus.COMPLETED;
  }

  public get assignedByUserName(): string {
    return this.assignedBy instanceof User ? this.assignedBy.name : undefined;
  }

  public get assignedByUserId(): number {
    return this.assignedBy instanceof User ? this.assignedBy.id : this.assignedBy;
  }

  public get userName(): string {
    return this.user instanceof User ? this.user.name : undefined;
  }

  public get userId(): number {
    return this.user instanceof User ? this.user.id : this.user;
  }

  isReviewBlocked(globalBlockedReview: boolean, userIsChair: boolean, userIsAuthor: boolean): boolean {
    if ((userIsChair && !userIsAuthor && !(this.status === ReviewStatus.DECLINED))) {
      return false;
    } else if (userIsChair && userIsAuthor) {
      return true;
    } else if (!userIsAuthor &&
      this.status === ReviewStatus.ASSIGNED ||
      this.status === ReviewStatus.DELEGATED ||
      this.status === ReviewStatus.NOTIFIED ||
      this.status === ReviewStatus.DECLINED ||
      this.status === ReviewStatus.DELETED ||
      this.submission.status === SubmissionStatus.ACCEPTED ||
      this.submission.status === SubmissionStatus.REJECTED ||
      this.submission.status === SubmissionStatus.WITHDRAWN ||
      (globalBlockedReview && (dayjs() > this.dueAt))
    ) {
      return true;
    }
    return false;
  }

  public get nonEditable(): boolean {
    return this.status === ReviewStatus.ASSIGNED || this.status === ReviewStatus.NOTIFIED;
  }
}

// Side-effect: register Submission with the shared class registry so
// `session.ts` (which uses `import type` to break the cycle) can
// resolve the runtime constructor via `getClass('Submission')`. See
// `_class-registry.ts` for the full rationale.
register('Submission', Submission);
